import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import Layout from '../../components/Layout';
import DashboardHeader from '../../components/DashboardHeader';
import FlagIcon from '../../components/FlagIcon';
import HansePayLoader from '../../components/HansePayLoader';
import ErrorToast from '../../components/ErrorToast';
import { ChevronDownIcon, CopyIcon, CheckIcon, FileTextIcon, DownloadIcon } from '../../assets/icons';
import * as balancesActions from '../../store/actions/balances';
import * as userActions from '../../store/actions/user';
import * as transactionsActions from '../../store/actions/transactions';
import {
  buildWalletAccounts,
  formatSpacedIdentifier,
  getDepositInstructionsCopy,
  getTransferMethodDetail,
} from '../../utils/accountsHelpers';
import {
  formatDepositAmount,
  formatDepositDate,
  getDepositTitle,
  getDepositsForCurrency,
  isDepositCompleted,
  normalizeDeposit,
} from '../../utils/depositHelpers';
import { openDepositReceipt } from '../../utils/depositReceipt';
import copyToClipboard from '../../utils/copyToClipboard';
import { openAccountDetailsPdf } from '../../utils/accountDetailsPdf';
import { openAccountStatementPdf, STATEMENT_FILTERS } from '../../utils/accountStatementPdf';
import { toTransactionList } from '../../utils/recipientHelpers';

const TABLE_CARD_CLASS =
  'overflow-hidden rounded-[14px] border border-[var(--color-border)] bg-[var(--white)] shadow-[var(--shadow-md)]';

const DOC_BUTTON_CLASS =
  'inline-flex items-center gap-2 rounded-full border border-[rgba(46,107,173,0.2)] bg-[var(--white)] px-4 py-2 text-[13px] font-medium text-[var(--n800)] transition-colors hover:bg-[rgba(46,107,173,0.05)] font-[family-name:var(--font-ui)] disabled:cursor-not-allowed disabled:opacity-50';

const DOC_BUTTON_ACTIVE_CLASS =
  'inline-flex items-center gap-2 rounded-full border border-[rgba(46,107,173,0.32)] bg-[var(--white)] px-4 py-2 text-[13px] font-medium text-[var(--n900)] shadow-[0_1px_2px_rgba(11,25,41,0.04)] font-[family-name:var(--font-ui)]';

const PANEL_LABEL_CLASS =
  'w-[96px] shrink-0 text-[11px] font-semibold uppercase tracking-[0.12em] text-[rgba(61,90,115,0.85)] font-[family-name:var(--font-ui)]';

const DEPOSITS_PER_PAGE = 4;

function DocumentIcon({ className = 'h-4 w-4' }) {
  return (
    <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24' aria-hidden='true'>
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth={1.8}
        d='M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z'
      />
      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.8} d='M14 2v6h6' />
    </svg>
  );
}

DocumentIcon.propTypes = {
  className: PropTypes.string,
};

const RAIL_TAB_CLASS =
  'rounded-[10px] border px-4 py-3 text-center text-[13px] font-semibold transition-colors font-[system-ui,-apple-system,BlinkMacSystemFont,sans-serif]';

const RAIL_TAB_SELECTED_CLASS =
  'border-[rgba(46,107,173,0.55)] bg-[rgba(46,107,173,0.1)] text-[var(--n900)]';

const RAIL_TAB_IDLE_CLASS =
  'border-[var(--color-border)] bg-[var(--white)] text-[var(--text-secondary)] hover:border-[rgba(46,107,173,0.28)] hover:text-[var(--n800)]';

function CurrencyGlyph({ currency }) {
  const code = String(currency || '').toUpperCase();
  let glyph = code.slice(0, 1);
  if (code === 'EUR') glyph = '€';
  if (code === 'USD') glyph = '$';
  if (code === 'GBP') glyph = '£';

  return (
    <div className='flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] bg-[var(--n900)] text-[18px] font-medium leading-none text-white'>
      {glyph}
    </div>
  );
}

CurrencyGlyph.propTypes = {
  currency: PropTypes.string.isRequired,
};

function CopyDetailRow({ label, value, copyValue, isMono, copied, onCopy }) {
  return (
    <div className='flex items-center gap-3 rounded-[10px] border border-[rgba(11,25,41,0.08)] bg-[var(--white)] px-4 py-3.5'>
      <div className='min-w-0 flex-1'>
        <div className='mb-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--n400)]'>{label}</div>
        <div
          className={`truncate text-[14px] font-medium text-[var(--n900)] [font-feature-settings:"tnum"] ${
            isMono ? 'font-mono' : ''
          }`}
        >
          {value}
        </div>
      </div>
      <button
        type='button'
        onClick={() => onCopy(label, copyValue || value)}
        aria-label={copied ? `Copied ${label}` : `Copy ${label}`}
        className='flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[var(--n400)] transition-colors hover:bg-[rgba(46,107,173,0.08)]'
      >
        {copied ? (
          <span className='flex h-3.5 w-3.5 items-center justify-center text-[var(--color-success)]'>
            <CheckIcon />
          </span>
        ) : (
          <CopyIcon className='h-3.5 w-3.5' strokeWidth={1.8} />
        )}
      </button>
    </div>
  );
}

CopyDetailRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  copyValue: PropTypes.string,
  isMono: PropTypes.bool,
  copied: PropTypes.bool.isRequired,
  onCopy: PropTypes.func.isRequired,
};

CopyDetailRow.defaultProps = {
  copyValue: undefined,
  isMono: false,
};

function formatDepositFields(bankAccount) {
  if (!bankAccount) return [];

  const fields = [];
  const holder =
    bankAccount.accountHolderName || bankAccount.beneficiaryName || bankAccount.name || '';
  const iban = String(bankAccount.iban || '').trim();
  const accountNumber = String(bankAccount.accountNumber || '').trim();
  const bic = String(bankAccount.bic || bankAccount.swiftCode || '').trim();
  const routing = String(bankAccount.routingNumber || '').trim();
  const sortCode = String(bankAccount.sortCode || '').trim();
  const type = String(bankAccount.type || '').toLowerCase();

  if (holder) {
    fields.push({
      label: 'Account holder',
      value: holder,
      isMono: false,
    });
  }
  if (iban) {
    fields.push({
      label: 'IBAN',
      value: formatSpacedIdentifier(iban),
      copyValue: iban.replace(/\s/g, ''),
      isMono: true,
    });
  } else if (accountNumber) {
    fields.push({
      label: 'Account number',
      value: formatSpacedIdentifier(accountNumber),
      copyValue: accountNumber.replace(/\s/g, ''),
      isMono: true,
    });
  }
  if (bic && (type === 'sepa' || type === 'swift' || iban)) {
    fields.push({
      label: type === 'sepa' ? 'BIC' : 'SWIFT / BIC',
      value: bic,
      copyValue: bic.replace(/\s/g, ''),
      isMono: true,
    });
  }
  if (routing && !iban) {
    fields.push({
      label: type === 'ach' ? 'ACH routing number' : 'Routing number',
      value: formatSpacedIdentifier(routing),
      copyValue: routing.replace(/\s/g, ''),
      isMono: true,
    });
  }
  if (sortCode) {
    fields.push({
      label: 'Sort code',
      value: sortCode,
      copyValue: sortCode.replace(/\s/g, ''),
      isMono: true,
    });
  }

  return fields;
}

function AccountRow({
  account,
  expanded,
  statementLoading,
  deposits,
  depositsLoading,
  onToggleExpand,
  onOpenStatement,
}) {
  const [copiedLabel, setCopiedLabel] = useState(null);
  const [selectedRailType, setSelectedRailType] = useState(account.preferredRailType);
  const [statementMenuOpen, setStatementMenuOpen] = useState(false);
  const [depositInstructionsOpen, setDepositInstructionsOpen] = useState(false);
  const [depositTransactionsOpen, setDepositTransactionsOpen] = useState(true);
  const [showAllDeposits, setShowAllDeposits] = useState(false);
  const statementMenuRef = useRef(null);

  useEffect(() => {
    setSelectedRailType(account.preferredRailType);
  }, [account.preferredRailType, account.currency]);

  useEffect(() => {
    if (!depositInstructionsOpen) {
      setCopiedLabel(null);
    }
  }, [depositInstructionsOpen, selectedRailType]);

  useEffect(() => {
    if (!statementMenuOpen) return undefined;
    const handleClickOutside = (event) => {
      if (statementMenuRef.current && !statementMenuRef.current.contains(event.target)) {
        setStatementMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [statementMenuOpen]);

  useEffect(() => {
    if (!expanded) {
      setStatementMenuOpen(false);
      setDepositInstructionsOpen(false);
      setDepositTransactionsOpen(true);
    }
  }, [expanded]);

  const activeRail =
    account.rails.find((rail) => String(rail.type).toLowerCase() === String(selectedRailType || '').toLowerCase()) ||
    account.rails[0] ||
    null;

  const depositFields = useMemo(() => formatDepositFields(activeRail?.bankAccount), [activeRail?.bankAccount]);

  const depositCopy = getDepositInstructionsCopy(activeRail?.bankAccount, account.currency, {
    railsCount: account.rails.length,
  });
  const transferHint = getTransferMethodDetail(activeRail?.bankAccount)?.hint || null;

  const accountProviders = useMemo(
    () =>
      (account.rails || [])
        .map((rail) => rail?.onRampProvider || rail?.bankAccount?.onRampProvider)
        .filter(Boolean),
    [account.rails],
  );

  const currencyDeposits = useMemo(
    () =>
      getDepositsForCurrency(deposits, account.currency, accountProviders)
        .map((deposit) => normalizeDeposit(deposit, account.currency))
        .filter(Boolean),
    [deposits, account.currency, accountProviders],
  );

  const hasMoreDeposits = currencyDeposits.length > DEPOSITS_PER_PAGE;
  const visibleDeposits = showAllDeposits ? currencyDeposits : currencyDeposits.slice(0, DEPOSITS_PER_PAGE);

  useEffect(() => {
    setShowAllDeposits(false);
  }, [account.currency, currencyDeposits.length]);

  const handleCopy = useCallback(async (label, text) => {
    const success = await copyToClipboard(text);
    if (!success) return;
    setCopiedLabel(label);
    window.setTimeout(() => {
      setCopiedLabel((current) => (current === label ? null : current));
    }, 2000);
  }, []);

  const handleOpenAccountDetailsPdf = useCallback(() => {
    try {
      openAccountDetailsPdf(account);
    } catch {
      // Popup blockers may prevent opening; fail silently.
    }
  }, [account]);

  const handleSelectStatementFilter = useCallback(
    (filter) => {
      setStatementMenuOpen(false);
      onOpenStatement(account, filter);
    },
    [account, onOpenStatement],
  );

  return (
    <div className='border-b border-[var(--color-border)] last:border-b-0'>
      <button
        type='button'
        onClick={onToggleExpand}
        aria-expanded={expanded}
        className='grid w-full grid-cols-[minmax(0,1.6fr)_minmax(120px,0.7fr)_auto] items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-[var(--off)]/70 sm:grid-cols-[minmax(0,1.6fr)_minmax(140px,0.7fr)_auto]'
      >
        <div className='flex min-w-0 items-center gap-3.5'>
          <CurrencyGlyph currency={account.currency} />
          <div className='min-w-0'>
            <div className='flex items-center gap-2'>
              <span className='truncate text-[15px] font-medium text-[var(--n900)] font-[family-name:var(--font-ui)]'>
                {account.title}
              </span>
              <FlagIcon currencyCode={account.currency} className='h-3.5 w-5 shrink-0 rounded-sm' />
            </div>
            {account.identifier && (
              <p className='mt-1 truncate text-[12.5px] tabular-nums text-[var(--text-tertiary)] font-[family-name:var(--font-ui)]'>
                {account.identifier}
              </p>
            )}
          </div>
        </div>

        <div className='text-right sm:text-left'>
          <div className='text-[15px] font-medium tabular-nums text-[var(--n900)] font-[family-name:var(--font-ui)]'>
            {account.amountLabel}
          </div>
          <div className='mt-0.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--n400)]'>
            {account.currency}
          </div>
        </div>

        <span
          className='flex h-8 w-8 shrink-0 items-center justify-center justify-self-end rounded-lg border border-[var(--color-border)] bg-[var(--white)] text-[var(--text-secondary)]'
          aria-hidden='true'
        >
          <svg width='16' height='16' viewBox='0 0 24 24' fill='currentColor' aria-hidden='true'>
            <circle cx='5' cy='12' r='1.75' />
            <circle cx='12' cy='12' r='1.75' />
            <circle cx='19' cy='12' r='1.75' />
          </svg>
        </span>
      </button>

      {expanded && (
        <div className='border-t border-[var(--color-border)]'>
          <div className='bg-[rgba(232,241,248,0.9)]'>
            <div className='flex flex-wrap items-center gap-x-5 gap-y-3 px-5 py-3.5'>
              <div className={PANEL_LABEL_CLASS}>Documents</div>
              <div className='flex flex-wrap items-center gap-2.5'>
                <button type='button' className={DOC_BUTTON_CLASS} onClick={handleOpenAccountDetailsPdf}>
                  <DocumentIcon className='h-4 w-4 text-[var(--n400)]' />
                  Account Details
                </button>
                <div className='relative' ref={statementMenuRef}>
                  <button
                    type='button'
                    className={statementMenuOpen ? DOC_BUTTON_ACTIVE_CLASS : DOC_BUTTON_CLASS}
                    onClick={() => setStatementMenuOpen((open) => !open)}
                    disabled={statementLoading}
                    aria-expanded={statementMenuOpen}
                    aria-haspopup='menu'
                  >
                    <FileTextIcon className='h-4 w-4 text-[var(--n400)]' />
                    {statementLoading ? 'Preparing Statement…' : 'Account Statement'}
                  </button>
                  {statementMenuOpen && (
                    <div
                      role='menu'
                      className='absolute left-0 z-50 mt-2 w-52 overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--white)] shadow-[var(--shadow-md)]'
                    >
                      {STATEMENT_FILTERS.map((option) => (
                        <button
                          key={option.id}
                          type='button'
                          role='menuitem'
                          onClick={() => handleSelectStatementFilter(option.id)}
                          className='block w-full px-4 py-2.5 text-left text-sm text-[var(--n800)] transition-colors hover:bg-[var(--off)] font-[family-name:var(--font-ui)]'
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className='border-t border-[rgba(46,107,173,0.12)] px-5 py-3.5'>
              <div className='flex flex-wrap items-center gap-x-5 gap-y-3'>
                <div className={PANEL_LABEL_CLASS}>Deposit</div>
                <div className='flex flex-wrap items-center gap-2.5'>
                  <button
                    type='button'
                    className={depositInstructionsOpen ? DOC_BUTTON_ACTIVE_CLASS : DOC_BUTTON_CLASS}
                    onClick={() => setDepositInstructionsOpen((open) => !open)}
                    aria-expanded={depositInstructionsOpen}
                  >
                    {depositInstructionsOpen ? 'Hide instructions' : 'Show instructions'}
                    <ChevronDownIcon
                      className={`h-3.5 w-3.5 text-[rgba(46,107,173,0.75)] transition-transform ${
                        depositInstructionsOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  <button
                    type='button'
                    className={depositTransactionsOpen ? DOC_BUTTON_ACTIVE_CLASS : DOC_BUTTON_CLASS}
                    onClick={() => setDepositTransactionsOpen((open) => !open)}
                    aria-expanded={depositTransactionsOpen}
                  >
                    {depositTransactionsOpen ? 'Hide transactions' : 'Show transactions'}
                    <ChevronDownIcon
                      className={`h-3.5 w-3.5 text-[rgba(46,107,173,0.75)] transition-transform ${
                        depositTransactionsOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {depositInstructionsOpen && (
            <div className='border-t border-[rgba(46,107,173,0.12)] bg-[var(--white)] px-5 py-5'>
              <p className='m-0 mb-4 max-w-2xl text-[13.5px] leading-6 text-[var(--text-secondary)] font-[family-name:var(--font-ui)]'>
                {depositCopy}
              </p>

              {account.rails.length > 1 && (
                <div className='mb-4 grid gap-2 sm:grid-cols-3'>
                  {account.rails.map((rail) => {
                    const selected =
                      String(rail.type).toLowerCase() === String(selectedRailType || '').toLowerCase();
                    return (
                      <button
                        key={rail.key}
                        type='button'
                        onClick={() => setSelectedRailType(rail.type)}
                        className={`${RAIL_TAB_CLASS} ${selected ? RAIL_TAB_SELECTED_CLASS : RAIL_TAB_IDLE_CLASS}`}
                      >
                        {rail.shortLabel}
                      </button>
                    );
                  })}
                </div>
              )}

              {depositFields.length === 0 ? (
                <p className='m-0 text-sm text-[var(--text-secondary)] font-[family-name:var(--font-ui)]'>
                  Deposit details are not available for this account yet.
                </p>
              ) : (
                <div className='space-y-2.5'>
                  {depositFields.map((field) => (
                    <CopyDetailRow
                      key={`${account.currency}-deposit-${selectedRailType || 'default'}-${field.label}`}
                      label={field.label}
                      value={field.value}
                      copyValue={field.copyValue}
                      isMono={field.isMono}
                      copied={copiedLabel === `deposit-${field.label}`}
                      onCopy={(label, text) => handleCopy(`deposit-${label}`, text)}
                    />
                  ))}
                </div>
              )}

              {transferHint && (
                <p className='m-0 mt-3.5 text-[12.5px] leading-5 text-[var(--n400)] font-[family-name:var(--font-ui)]'>
                  {transferHint}
                </p>
              )}
            </div>
          )}

          {depositTransactionsOpen && (
            <div className='border-t border-[rgba(46,107,173,0.12)] bg-[rgba(246,249,252,0.85)] px-5 py-4'>
              <div className='mb-3 text-[10px] font-semibold uppercase tracking-[0.12em] text-[rgba(61,90,115,0.85)] font-[family-name:var(--font-ui)]'>
                Recent deposits
              </div>

              {depositsLoading ? (
                <div className='space-y-2' role='status' aria-live='polite' aria-label='Loading deposits'>
                  {[0, 1, 2].map((key) => (
                    <div
                      key={key}
                      className='grid grid-cols-[96px_minmax(0,1fr)_auto_auto] items-center gap-3 rounded-[10px] border border-[var(--color-border)] bg-[var(--white)] px-3.5 py-3 sm:grid-cols-[110px_minmax(0,1fr)_auto_auto]'
                    >
                      <div className='h-3 w-16 animate-pulse rounded bg-[var(--off)]' />
                      <div className='h-3.5 w-28 animate-pulse rounded bg-[var(--off)]' />
                      <div className='h-3.5 w-20 animate-pulse rounded bg-[var(--off)]' />
                      <div className='h-8 w-8 animate-pulse rounded-lg bg-[var(--off)]' />
                    </div>
                  ))}
                </div>
              ) : currencyDeposits.length === 0 ? (
                <p className='m-0 text-sm text-[var(--text-secondary)] font-[family-name:var(--font-ui)]'>
                  No deposits yet for this account.
                </p>
              ) : (
                <>
                  <div className='space-y-2'>
                    {visibleDeposits.map((deposit) => {
                      const completed = isDepositCompleted(deposit);
                      return (
                        <div
                          key={deposit.id}
                          className='grid grid-cols-[96px_minmax(0,1fr)_auto_auto] items-center gap-3 rounded-[10px] border border-[var(--color-border)] bg-[var(--white)] px-3.5 py-3 sm:grid-cols-[110px_minmax(0,1fr)_auto_auto]'
                        >
                          <div className='text-[12.5px] text-[var(--text-tertiary)] font-[family-name:var(--font-ui)]'>
                            {formatDepositDate(deposit.creditedAt || deposit.requestedAt)}
                          </div>
                          <div className='min-w-0 truncate text-[13.5px] font-medium text-[var(--n900)] font-[system-ui,-apple-system,BlinkMacSystemFont,sans-serif]'>
                            {getDepositTitle(deposit)}
                          </div>
                          <div
                            className={`flex items-center gap-2 whitespace-nowrap text-[13px] font-semibold tabular-nums font-[system-ui,-apple-system,BlinkMacSystemFont,sans-serif] ${
                              completed ? 'text-[var(--color-success)]' : 'text-[var(--text-secondary)]'
                            }`}
                          >
                            <span
                              className={`h-1.5 w-1.5 rounded-full ${
                                completed ? 'bg-[var(--color-success)]' : 'bg-[var(--text-tertiary)]'
                              }`}
                              aria-hidden='true'
                            />
                            {formatDepositAmount(deposit.amount, deposit.currency)}
                          </div>
                          <button
                            type='button'
                            className='inline-flex h-8 w-8 items-center justify-center rounded-lg border border-[var(--color-border)] bg-[var(--white)] text-[var(--n400)] transition-colors hover:bg-[var(--off)] hover:text-[var(--n800)]'
                            aria-label='Download deposit confirmation'
                            onClick={() => openDepositReceipt(deposit)}
                          >
                            <DownloadIcon className='h-4 w-4' />
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  {hasMoreDeposits && (
                    <button
                      type='button'
                      onClick={() => setShowAllDeposits((open) => !open)}
                      className='mt-3 inline-flex items-center gap-1.5 text-[12.5px] font-medium text-[var(--n400)] transition-colors hover:text-[var(--n800)] font-[family-name:var(--font-ui)]'
                    >
                      {showAllDeposits
                        ? 'Show less'
                        : `Show ${currencyDeposits.length - DEPOSITS_PER_PAGE} more`}
                      <ChevronDownIcon
                        className={`h-3.5 w-3.5 transition-transform ${showAllDeposits ? 'rotate-180' : ''}`}
                      />
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

AccountRow.propTypes = {
  account: PropTypes.shape({
    currency: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    amountLabel: PropTypes.string.isRequired,
    identifier: PropTypes.string,
    preferredRailType: PropTypes.string,
    rails: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
        shortLabel: PropTypes.string.isRequired,
        bankAccount: PropTypes.shape({
          type: PropTypes.string,
          iban: PropTypes.string,
          accountNumber: PropTypes.string,
          routingNumber: PropTypes.string,
          accountHolderName: PropTypes.string,
          beneficiaryName: PropTypes.string,
          name: PropTypes.string,
          swiftCode: PropTypes.string,
          bic: PropTypes.string,
          bankName: PropTypes.string,
          accountType: PropTypes.string,
          sortCode: PropTypes.string,
        }),
      }),
    ).isRequired,
  }).isRequired,
  expanded: PropTypes.bool.isRequired,
  statementLoading: PropTypes.bool,
  deposits: PropTypes.arrayOf(PropTypes.object),
  depositsLoading: PropTypes.bool,
  onToggleExpand: PropTypes.func.isRequired,
  onOpenStatement: PropTypes.func.isRequired,
};

AccountRow.defaultProps = {
  statementLoading: false,
  deposits: [],
  depositsLoading: false,
};

function Accounts() {
  const dispatch = useDispatch();
  const { balances, gettingBalances, getBalancesError } = useSelector((state) => state.balances);
  const { onRampAccounts, onRampDeposits, gettingBankAccount, gettingOnRampDeposits, getBankAccountError, user } =
    useSelector((state) => state.user);
  const { supportedOnRampCurrencies } = useSelector((state) => state.config);
  const { transactions, gettingTransactions } = useSelector((state) => state.transactions);

  const [expandedCurrencies, setExpandedCurrencies] = useState(() => new Set());
  const [statementAccount, setStatementAccount] = useState(null);
  const statementPending = useRef(null);

  const statementParties = useMemo(() => {
    const personName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
    const fullName = user?.userDetails?.fullName?.trim() || '';
    const managerName = personName || fullName || '';
    const managerEmail = user?.email || '';
    const companyName = user?.company?.registeredName?.trim() || user?.company?.tradeName?.trim() || '';
    const accountOwner = companyName || managerName || '';
    const accountManager =
      managerName || managerEmail ? { name: managerName, email: managerEmail } : null;
    return { accountOwner: accountOwner || null, accountManager };
  }, [user]);

  useEffect(() => {
    dispatch(balancesActions.getBalances());
    dispatch(userActions.getBankAccount());
    dispatch(transactionsActions.getTransactions());
  }, [dispatch]);

  useEffect(() => {
    if (!statementAccount || gettingTransactions || !statementPending.current) return;

    const { account, filter } = statementPending.current;
    statementPending.current = null;
    try {
      openAccountStatementPdf(account, toTransactionList(transactions), {
        filter,
        accountOwner: statementParties.accountOwner,
        accountManager: statementParties.accountManager,
      });
    } catch {
      // Popup blockers may prevent opening; fail silently.
    }
    setStatementAccount(null);
  }, [statementAccount, gettingTransactions, transactions, statementParties]);

  const handleOpenStatement = useCallback(
    (account, filter = 'all') => {
      const loaded = toTransactionList(transactions);
      const personName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
      const fullName = user?.userDetails?.fullName?.trim() || '';
      const managerName = personName || fullName || '';
      const managerEmail = user?.email || '';
      const companyName = user?.company?.registeredName?.trim() || user?.company?.tradeName?.trim() || '';
      const accountOwner = companyName || managerName || null;
      const accountManager =
        managerName || managerEmail ? { name: managerName, email: managerEmail } : null;

      if (loaded.length > 0 && !gettingTransactions) {
        try {
          openAccountStatementPdf(account, loaded, {
            filter,
            accountOwner,
            accountManager,
          });
        } catch {
          // Popup blockers may prevent opening; fail silently.
        }
        return;
      }

      statementPending.current = { account, filter };
      setStatementAccount(account);
      if (!gettingTransactions) {
        dispatch(transactionsActions.getTransactions());
      }
    },
    [dispatch, gettingTransactions, transactions, user],
  );

  const accounts = useMemo(
    () =>
      buildWalletAccounts({
        balances,
        onRampAccounts,
        supportedCurrencies: supportedOnRampCurrencies,
      }),
    [balances, onRampAccounts, supportedOnRampCurrencies],
  );

  const loading = (gettingBalances || gettingBankAccount) && accounts.length === 0;
  const error = getBalancesError || getBankAccountError;

  const handleToggleExpand = (currency) => {
    setExpandedCurrencies((current) => {
      const next = new Set(current);
      if (next.has(currency)) {
        next.delete(currency);
      } else {
        next.add(currency);
      }
      return next;
    });
  };

  return (
    <Layout hideHeader mainPaddingClassName='px-6 py-9 sm:px-11'>
      <div className='flex flex-col gap-7'>
        <div>
          <DashboardHeader title='Your Accounts' />
          <p className='mt-2 max-w-xl text-sm text-[var(--text-secondary)] font-[family-name:var(--font-ui)]'>
            Please find an overview of your accounts.
          </p>
        </div>

        {loading ? (
          <div className='flex justify-center py-16'>
            <HansePayLoader />
          </div>
        ) : (
          <div className={TABLE_CARD_CLASS}>
            <div className='grid grid-cols-[minmax(0,1.6fr)_minmax(120px,0.7fr)_auto] gap-4 border-b border-[var(--color-border)] px-5 py-3 sm:grid-cols-[minmax(0,1.6fr)_minmax(140px,0.7fr)_auto]'>
              <div className='text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--n400)]'>Account</div>
              <div className='text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--n400)]'>Amount</div>
              <div className='justify-self-end self-center text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--n400)]'>
                More
              </div>
            </div>

            {accounts.length === 0 ? (
              <div className='px-5 py-12 text-center text-sm text-[var(--text-secondary)] font-[family-name:var(--font-ui)]'>
                No accounts available yet.
              </div>
            ) : (
              accounts.map((account) => (
                <AccountRow
                  key={account.id}
                  account={account}
                  expanded={expandedCurrencies.has(account.currency)}
                  statementLoading={Boolean(statementAccount?.currency === account.currency)}
                  deposits={onRampDeposits}
                  depositsLoading={gettingOnRampDeposits}
                  onToggleExpand={() => handleToggleExpand(account.currency)}
                  onOpenStatement={handleOpenStatement}
                />
              ))
            )}

            {accounts.length > 0 && (
              <div className='border-t border-[var(--color-border)] px-5 py-3 text-[12px] text-[var(--text-tertiary)] font-[family-name:var(--font-ui)]'>
                Showing {accounts.length} of {accounts.length} accounts
              </div>
            )}
          </div>
        )}
      </div>

      <ErrorToast error={error} />
    </Layout>
  );
}

export default Accounts;
